/**
 * assets/ad-product-pdp.js — Atelier Diamante PDP controller
 *
 * Two layers:
 *  1. Alpine.js component "adProductPDP()" — drives all x-data bindings in
 *     ad-product-pdp.liquid (tabs, viewer, accordions, cart, zoom).
 *  2. Vanilla JS init() — drives compare badge (data-compare-add).
 */

/* ═══════════════════════════════════════════════════════════════════════════
   ALPINE COMPONENT — registered before Alpine initialises via alpine:init
   ═══════════════════════════════════════════════════════════════════════════ */

document.addEventListener('alpine:init', function () {
  Alpine.data('adProductPDP', function () {
    return {

      /* ── State ─────────────────────────────────────────────────────── */
      activeMediaTab:   'photo',
      activeImageIndex: 0,
      viewerLoaded:     false,
      isAddingToCart:   false,
      showStickyAtc:    false,
      expandedSections: { details: false, shipping: false, care: false },

      /* ── Lifecycle ─────────────────────────────────────────────────── */
      init: function () {
        // Sticky ATC bar — slides up when the main CTA leaves viewport
        var self     = this;
        var ctaStack = this.$el.querySelector('.ad-product-pdp__cta-stack');
        if (ctaStack && 'IntersectionObserver' in window) {
          new IntersectionObserver(function (entries) {
            self.showStickyAtc = !entries[0].isIntersecting;
            var bar = self.$el.querySelector('.ad-pdp-sticky-atc');
            if (bar) bar.setAttribute('aria-hidden', self.showStickyAtc ? 'false' : 'true');
          }, { threshold: 0, rootMargin: '0px 0px -56px 0px' }).observe(ctaStack);
        }
      },

      /* ── Media tabs ────────────────────────────────────────────────── */
      switchMedia: function (tab) {
        this.activeMediaTab = tab;
        // Lazy-load: mark viewer as loaded on first 360 switch
        // (iframe :src binding reacts to viewerLoaded in the template)
        if (tab === '360') this.viewerLoaded = true;

        // Lazy-start model-viewer on first switch to 3d tab
        if (tab === '3d') {
          var mv = this.$el.querySelector('model-viewer[data-src]');
          if (mv && !mv.getAttribute('src')) {
            mv.setAttribute('src', mv.dataset.src);
          }
        }
      },

      /* ── Thumbnail rail ────────────────────────────────────────────── */
      switchImage: function (idx) {
        this.activeImageIndex = idx;
        this.activeMediaTab   = 'photo';
      },

      /* ── Image zoom (desktop hover) ────────────────────────────────── */
      handleImageZoom: function (e) {
        // Only on non-touch devices
        if (window.matchMedia('(hover: none)').matches) return;
        var container = e.currentTarget;
        var img       = container.querySelector('.ad-product-pdp__featured-image');
        if (!img) return;
        var rect = container.getBoundingClientRect();
        var x    = ((e.clientX - rect.left)  / rect.width)  * 100;
        var y    = ((e.clientY - rect.top)   / rect.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
        img.style.transform       = 'scale(1.75)';
      },

      resetImageZoom: function () {
        var img = this.$el.querySelector('.ad-product-pdp__featured-image');
        if (img) { img.style.transform = ''; img.style.transformOrigin = ''; }
      },

      /* ── Accordions ────────────────────────────────────────────────── */
      toggleSection: function (name) {
        this.expandedSections[name] = !this.expandedSections[name];
      },

      /* ── Add to Cart ───────────────────────────────────────────────── */
      handleAddToCart: function () {
        if (this.isAddingToCart) return;
        var variantId = this.$el.dataset.variantId;
        if (!variantId) return;

        var self          = this;
        this.isAddingToCart = true;

        fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
        })
        .then(function (r) { return r.json(); })
        .then(function () {
          // Open the cart drawer if present
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('cart:open'));
        })
        .catch(function (err) { console.error('Cart add failed:', err); })
        .finally(function () { self.isAddingToCart = false; });
      },

      /* ── Consultation CTA ──────────────────────────────────────────── */
      handleConsultationClick: function () {
        // Scroll to consultation form if on same page, else navigate
        var form = document.getElementById('ad-consult-form');
        if (form) {
          form.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.location.href = '/pages/consult';
        }
      }

    }; // end Alpine.data return
  }); // end Alpine.data
}); // end alpine:init


/* ═══════════════════════════════════════════════════════════════════════════
   VANILLA JS — compare badge wiring (not handled by Alpine)
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  function init() {
    const el = document.querySelector('.ad-product-pdp');
    if (!el) return;

    const productId = el.dataset.productId;
    const images    = loadImages(productId);

    initTabs(el);
    initThumbs(el, images);
    initZoom(el);
    initAccordions();
    initStickyBar(el);
  }

  /* ── Image manifest ──────────────────────────────────────────────────── */

  function loadImages(productId) {
    const script = document.getElementById('pdp-images-' + productId);
    if (!script) return [];
    try { return JSON.parse(script.textContent); } catch (e) { return []; }
  }

  /* ── Media tabs (Photo / 360°) ───────────────────────────────────────── */

  function initTabs(el) {
    const tabs   = el.querySelectorAll('[data-pdp-tab]');
    const panels = el.querySelectorAll('[data-pdp-panel]');
    if (!tabs.length) return;

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        const target = tab.dataset.pdpTab;

        tabs.forEach(function (t) {
          t.classList.toggle('is-active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });
        panels.forEach(function (p) {
          p.hidden = p.dataset.pdpPanel !== target;
        });

        // Lazy-load 360 iframe on first request
        if (target === 'view360') {
          const iframe = el.querySelector('[data-pdp-viewer-src]');
          if (iframe && iframe.src === 'about:blank') {
            iframe.src = iframe.dataset.pdpViewerSrc;
          }
        }
      });
    });
  }

  /* ── Thumbnail rail ─────────────────────────────────────────────────── */

  function initThumbs(el, images) {
    const thumbs = el.querySelectorAll('[data-pdp-thumb]');
    const mainImg = el.querySelector('[data-pdp-main-img]');
    if (!thumbs.length || !mainImg) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        const idx = parseInt(thumb.dataset.pdpThumb, 10);
        const img = images[idx];
        if (img) {
          mainImg.src    = img.src;
          mainImg.srcset = '';
        }
        thumbs.forEach(function (t) {
          t.classList.toggle('is-active', t === thumb);
        });
        // Switch back to photo tab if 360 is active
        const photoPanel = el.querySelector('[data-pdp-panel="photo"]');
        const view360    = el.querySelector('[data-pdp-panel="view360"]');
        if (photoPanel) photoPanel.hidden = false;
        if (view360)    view360.hidden    = true;
        const tabs = el.querySelectorAll('[data-pdp-tab]');
        tabs.forEach(function (t) {
          t.classList.toggle('is-active', t.dataset.pdpTab === 'photo');
          t.setAttribute('aria-selected', t.dataset.pdpTab === 'photo' ? 'true' : 'false');
        });
      });
    });
  }

  /* ── Zoom on hover (desktop only) ───────────────────────────────────── */

  function initZoom(el) {
    const container = el.querySelector('[data-pdp-zoom-container]');
    const img       = el.querySelector('[data-pdp-main-img]');
    if (!container || !img) return;
    if (window.matchMedia('(hover: none)').matches) return; // skip touch devices

    container.addEventListener('mousemove', function (e) {
      const rect = container.getBoundingClientRect();
      const x    = ((e.clientX - rect.left)  / rect.width)  * 100;
      const y    = ((e.clientY - rect.top)   / rect.height) * 100;
      img.style.transformOrigin = x + '% ' + y + '%';
      img.style.transform       = 'scale(1.75)';
    });
    container.addEventListener('mouseleave', function () {
      img.style.transform       = '';
      img.style.transformOrigin = '';
    });
  }

  /* ── Accordions ─────────────────────────────────────────────────────── */

  function initAccordions() {
    document.querySelectorAll('[data-pdp-accordion-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
      });
    });
  }

  /* ── Sticky mobile enquiry bar ──────────────────────────────────────── */

  function initStickyBar(el) {
    const bar      = document.querySelector('.ad-pdp-sticky-atc');
    const ctaStack = el.querySelector('.ad-product-pdp__cta-stack');
    if (!bar || !ctaStack || !('IntersectionObserver' in window)) return;

    new IntersectionObserver(function (entries) {
      const visible = !entries[0].isIntersecting;
      bar.dataset.visible = visible ? 'true' : 'false';
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }, { threshold: 0, rootMargin: '0px 0px -56px 0px' }).observe(ctaStack);
  }

  /* ── Boot ───────────────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
